/*
 * Eloqua newsletter subscription form.
 *
 * Validation comes from Eloqua's own LiveValidation build, loaded from their CDN.
 * That library MUST only ever execute once per page. It opens with a bare
 * `var LiveValidation = ...` and has no reload guard, so a second execution
 * replaces the `LiveValidation` / `Validate` / `LiveValidationForm` globals with
 * brand-new objects and orphans every validator built against the first copy.
 *
 * The failure that causes is quiet and easy to misread. `add()` stores the rule
 * as `{type: Validate.Presence}` — a reference captured at call time — while
 * `doValidations` dispatches on it with `switch (t.type) { case Validate.Presence: … }`
 * against the *live* global. After a second load no case matches, so it falls to
 * `default` and never sets `displayMessageWhenEmpty`; `validateElement` then
 * swallows the failure for an empty value. Format/HTML/URL rules keep working,
 * so the form still reports bad input while silently accepting blank required
 * fields — which is why this previously looked like "one form validates, the
 * other doesn't" when two widgets shared a page.
 *
 * Hence: one memoised load here, then every instance wired against that copy.
 */

type NewsletterMessages = {
  required: string;
  noHtml: string;
  noUrls: string;
  emailFormat: string;
};

type NewsletterSubscription = {
  init: () => void;
};

const LIB_SRC = 'https://img04.en25.com/i/livevalidation_standalone.compressed.js';

const CONTAINS_HTML = /(<([^>]+)>)/gi;
const CONTAINS_URL =
  /(telnet|ftp|https?):\/\/(?:[a-z0-9][a-z0-9-]{0,61}[a-z0-9]\.|[a-z0-9]\.)+[a-z]{2,63}/i;
const EMAIL_FORMAT =
  /(^[A-Z0-9!#\$%&'\*\+\-\/=\?\^_`\{\|\}~][A-Z0-9!#\$%&'\*\+\-\/=\?\^_`\{\|\}~\.]{0,62}@(([A-Z0-9](?:[A-Z0-9\-]{0,61}[A-Z0-9])?)(\.[A-Z0-9](?:[A-Z0-9\-]{0,61}[A-Z0-9])?)+)$)/i;
const EMAIL_CONSECUTIVE_DOTS = /\.\.|\.@/i;

const DEFAULT_MESSAGES: NewsletterMessages = {
  required: 'This field is required',
  noHtml: 'Value must not contain any HTML',
  noUrls: "Value must not contain any URL's",
  emailFormat: 'A valid email address is required',
};

/** Resolved once, shared by every widget on the page. */
let libPromise: Promise<void> | null = null;

const loadLiveValidation = (): Promise<void> => {
  if (libPromise) return libPromise;

  libPromise = new Promise<void>((resolve, reject) => {
    // Another script on the page may already have pulled the library in — the
    // Eloqua embed code editors paste into the Campaign hero banner ships its
    // own copy. Reusing it is the whole point of this guard.
    if ((window as any).LiveValidation) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = LIB_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Unable to load ${LIB_SRC}`));
    document.head.appendChild(script);
  });

  return libPromise;
};

const parseMessages = (widget: HTMLElement): NewsletterMessages => {
  if (!widget.dataset.options) return DEFAULT_MESSAGES;

  try {
    return { ...DEFAULT_MESSAGES, ...JSON.parse(widget.dataset.options) };
  } catch {
    return DEFAULT_MESSAGES;
  }
};

/**
 * Fills the hidden elqCustomerGUID inputs once Eloqua's tracking script has
 * published GetElqCustomerGUID. Runs once per page rather than per widget —
 * the function it waits on is global, so polling for it per instance only
 * multiplies the timers.
 */
const trackCustomerGuid = () => {
  const maxAttempts = 5;
  let attempts = 0;

  const apply = () => {
    const guid = (window as any).GetElqCustomerGUID();
    document
      .querySelectorAll<HTMLInputElement>('input[name="elqCustomerGUID"]')
      .forEach((field) => {
        field.value = guid;
      });
  };

  const poll = () => {
    if (typeof (window as any).GetElqCustomerGUID === 'function') {
      apply();
      return;
    }
    attempts += 1;
    if (attempts >= maxAttempts) return;
    window.setTimeout(poll, 500);
  };

  if (Array.isArray((window as any)._elqQ)) {
    (window as any)._elqQ.push(['elqGetCustomerGUID']);
  }

  window.setTimeout(poll, 500);
};

/**
 * Wires everything that does not depend on the validation library, so the form
 * still submits and still reports success if the CDN is unreachable.
 */
const wireForm = (widget: HTMLElement) => {
  const form = widget.querySelector('form');
  const container = widget.querySelector<HTMLElement>(
    '[data-hook=newsletterSubscription__container]',
  );
  const success = widget.querySelector<HTMLElement>(
    '[data-hook=newsletterSubscription__success]',
  );
  const iframe = widget.querySelector<HTMLIFrameElement>(
    '[data-hook=newsletterSubscription__iframe]',
  );
  if (!form || !container || !success || !iframe) return;

  const submitButton = form.querySelector<HTMLInputElement>('input[type=submit]');

  const showSpinner = () => {
    if (!submitButton || !submitButton.parentNode) return;
    const spinner = document.createElement('span');
    spinner.setAttribute('class', 'loader');
    submitButton.disabled = true;
    submitButton.style.cursor = 'wait';
    submitButton.parentNode.appendChild(spinner);
  };

  const clearSpinner = () => {
    if (!submitButton || !submitButton.parentNode) return;
    submitButton.disabled = false;
    submitButton.style.cursor = '';
    const loader = (submitButton.parentNode as HTMLElement).querySelector('.loader');
    loader?.remove();
  };

  // The form posts cross-origin into a hidden iframe. We can't read the
  // response, but the throw we get from touching its location tells us Eloqua
  // has navigated it — i.e. the post went through.
  const armSuccessDetection = () => {
    const handleLoad = () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        iframe.contentWindow?.location.href;
        return;
      } catch {
        // Cross-origin: Eloqua has rendered its own page in the iframe.
      }
      iframe.removeEventListener('load', handleLoad);
      container.style.display = 'none';
      success.style.display = '';
    };
    iframe.addEventListener('load', handleLoad);
  };

  form.querySelectorAll('input, select, textarea').forEach((field) => {
    field.addEventListener('change', clearSpinner);
  });

  // Assigned as the onsubmit *property*, deliberately, and before any
  // LiveValidation instance exists. LiveValidationForm captures whatever is on
  // the property as `oldOnSubmit` and then short-circuits it:
  //
  //   element.onsubmit = () => massValidate(fields) && oldOnSubmit.call(...) !== false
  //
  // so this only runs once validation has actually passed. A plain
  // addEventListener('submit') listener would still fire on a rejected submit
  // (returning false prevents the default, it does not stop other listeners),
  // leaving a spinner running and the iframe handler armed for a post that
  // never happened.
  form.onsubmit = () => {
    showSpinner();
    armSuccessDetection();
    return true;
  };
};

const wireValidation = (widget: HTMLElement) => {
  const LiveValidation = (window as any).LiveValidation;
  const Validate = (window as any).Validate;
  if (!LiveValidation || !Validate) return;

  const form = widget.querySelector('form');
  if (!form) return;

  const messages = parseMessages(widget);

  const validateField = (name: string): any => {
    const field = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    if (!field) return null;

    const validation = new LiveValidation(field, {
      validMessage: '',
      onlyOnBlur: false,
      wait: 300,
      isPhoneField: false,
    });

    validation.add(Validate.Presence, { failureMessage: messages.required });
    validation.add(Validate.Custom, {
      against: (value: string) => !value.match(CONTAINS_HTML),
      failureMessage: messages.noHtml,
    });
    validation.add(Validate.Custom, {
      against: (value: string) => !value.match(CONTAINS_URL),
      failureMessage: messages.noUrls,
    });

    return validation;
  };

  validateField('first_name');
  validateField('last_name');

  const email = validateField('email_address');
  if (!email) return;

  email.add(Validate.Format, {
    pattern: EMAIL_FORMAT,
    failureMessage: messages.emailFormat,
  });
  email.add(Validate.Format, {
    pattern: EMAIL_CONSECUTIVE_DOTS,
    failureMessage: messages.emailFormat,
    negate: 'true',
  });
};

const NewsletterSubscription: NewsletterSubscription = {
  init() {
    const widgets = document.querySelectorAll<HTMLElement>(
      '[data-hook=newsletterSubscription]',
    );
    if (!widgets.length) return;

    widgets.forEach((widget) => wireForm(widget));
    trackCustomerGuid();

    loadLiveValidation()
      .then(() => widgets.forEach((widget) => wireValidation(widget)))
      .catch(() => {
        // Nothing to do: the inputs carry the native `required` attribute, so
        // the browser still blocks an empty submit without us.
      });
  },
};

export default NewsletterSubscription;
